import type { Field, GroupField } from 'payload'

import deepMerge from '@/utilities/deepMerge'

export type LinkAppearances = 'green' | 'black' | 'blackOutline' | 'blackOutlineMinimal' | 'whiteOutline' | 'white'

// Legacy appearance values for backwards compatibility
export type LegacyLinkAppearances = 'default' | 'outline'

// Type representing the actual data structure of a link field
export type LinkFieldType = {
  type?: ('reference' | 'custom' | 'chat' | 'jumpToBlock') | null
  newTab?: boolean | null
  reference?: {
    relationTo: 'pages' | 'posts' | 'webinars' | 'platform-engineering-unplugged'
    value: string | { slug?: string | null }
  } | null
  url?: string | null
  anchorId?: string | null
  label?: string | null
  appearance?: LinkAppearances | LegacyLinkAppearances | null
}

export const appearanceOptions: Record<LinkAppearances, { label: string; value: string }> = {
  green: {
    label: 'Green (Primary)',
    value: 'green',
  },
  black: {
    label: 'Black',
    value: 'black',
  },
  blackOutline: {
    label: 'Black Outline',
    value: 'blackOutline',
  },
  blackOutlineMinimal: {
    label: 'Black Outline (No Circle)',
    value: 'blackOutlineMinimal',
  },
  whiteOutline: {
    label: 'White Outline',
    value: 'whiteOutline',
  },
  white: {
    label: 'White',
    value: 'white',
  },
}

/**
 * Maps legacy appearance values to new values for backwards compatibility.
 * Use this function when rendering buttons to ensure existing content works.
 */
export const mapLegacyAppearance = (
  appearance: LinkAppearances | LegacyLinkAppearances | null | undefined,
): LinkAppearances => {
  if (!appearance) return 'green'

  const legacyMap: Record<string, LinkAppearances> = {
    default: 'green',
    outline: 'blackOutline',
  }

  return legacyMap[appearance] ?? (appearance as LinkAppearances)
}

type LinkType = (options?: {
  appearances?: LinkAppearances[] | false
  disableLabel?: boolean
  labelRequired?: boolean
  overrides?: Partial<GroupField>
}) => Field

export const link: LinkType = ({ appearances, disableLabel = false, labelRequired = true, overrides = {} } = {}) => {
  const linkResult: GroupField = {
    name: 'link',
    type: 'group',
    admin: {
      hideGutter: true,
    },
    fields: [
      {
        type: 'row',
        fields: [
          {
            name: 'type',
            type: 'radio',
            admin: {
              layout: 'horizontal',
              width: '50%',
            },
            defaultValue: 'reference',
            options: [
              {
                label: 'Internal link',
                value: 'reference',
              },
              {
                label: 'Custom URL',
                value: 'custom',
              },
              {
                label: 'Open HubSpot Chat',
                value: 'chat',
              },
              {
                label: 'Jump to block',
                value: 'jumpToBlock',
              },
            ],
          },
          {
            name: 'newTab',
            type: 'checkbox',
            admin: {
              condition: (_, siblingData) => siblingData?.type !== 'chat' && siblingData?.type !== 'jumpToBlock',
              style: {
                alignSelf: 'flex-end',
              },
              width: '50%',
            },
            label: 'Open in new tab',
          },
        ],
      },
    ],
  }

  const linkTypes: Field[] = [
    {
      name: 'reference',
      type: 'relationship',
      admin: {
        condition: (_, siblingData) => siblingData?.type === 'reference',
      },
      label: 'Document to link to',
      relationTo: ['pages', 'posts', 'webinars', 'platform-engineering-unplugged'],
      validate: (value: unknown, { siblingData }: { siblingData?: Record<string, unknown> }) => {
        // Only require if there's a label (CTA is being used)
        if (siblingData?.type === 'reference' && siblingData?.label && !value) {
          return 'This field is required when using internal links.'
        }
        return true
      },
    },
    {
      name: 'url',
      type: 'text',
      admin: {
        condition: (_, siblingData) => siblingData?.type === 'custom',
      },
      label: 'Custom URL',
      validate: (value: unknown, { siblingData }: { siblingData?: Record<string, unknown> }) => {
        // Only require if there's a label (CTA is being used)
        if (siblingData?.type === 'custom' && siblingData?.label && !value) {
          return 'This field is required when using custom URLs.'
        }
        return true
      },
    },
    {
      name: 'anchorId',
      type: 'text',
      admin: {
        condition: (_, siblingData) => siblingData?.type === 'jumpToBlock',
        description: 'Select a block anchor to jump to',
        components: {
          Field: '@/fields/anchorPicker/AnchorPicker#AnchorPicker',
        },
      },
      label: 'Jump to Anchor',
      validate: (value: unknown, { siblingData }: { siblingData?: Record<string, unknown> }) => {
        // Require anchorId if there's a label (CTA is being used)
        if (siblingData?.type === 'jumpToBlock' && siblingData?.label && !value) {
          return 'Please select an anchor to jump to.'
        }
        return true
      },
    },
  ]

  if (!disableLabel) {
    linkTypes.map((linkType) => ({
      ...linkType,
      admin: {
        ...linkType.admin,
        width: '50%',
      },
    }))

    linkResult.fields.push({
      type: 'row',
      fields: [
        ...linkTypes,
        {
          name: 'label',
          type: 'text',
          admin: {
            width: '50%',
          },
          label: 'Label',
          required: labelRequired,
        },
      ],
    })
  } else {
    linkResult.fields = [...linkResult.fields, ...linkTypes]
  }

  if (appearances !== false) {
    let appearanceOptionsToUse = [
      appearanceOptions.green,
      appearanceOptions.black,
      appearanceOptions.blackOutline,
      appearanceOptions.whiteOutline,
      appearanceOptions.white,
    ]

    if (appearances) {
      appearanceOptionsToUse = appearances.map((appearance) => appearanceOptions[appearance])
    }

    linkResult.fields.push({
      name: 'appearance',
      type: 'select',
      admin: {
        description: 'Choose how the link should be rendered.',
      },
      defaultValue: 'green',
      options: appearanceOptionsToUse,
      hooks: {
        // Migrate legacy appearance values before validation
        beforeValidate: [
          ({ value }) => {
            if (!value) return 'green'
            // Map legacy values to new values
            const legacyMap: Record<string, string> = {
              default: 'green',
              outline: 'blackOutline',
            }
            return legacyMap[value] ?? value
          },
        ],
      },
    })
  }

  return deepMerge(linkResult, overrides)
}
